import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import EditBlueprintWizard from '@/components/wizards/blueprints/blueprint_wizard/EditBlueprintWizard';
import { GlossaryBlueprint } from '@/types-games';
import { updateTemplate, deleteTemplate, createTemplate } from "@/lib/stores/templatesStore";
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';
import { useTabContext } from '@/contexts/TabContext';
import { useTabTitleSync } from '@/hooks/useTabTitleSync';
import { BlueprintData } from '@/components/wizards/blueprints/types';
import { useConfirmation } from '@/hooks/use-confirmation';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { getPendingChangeById, removePendingChange, subscribeToPendingChanges } from '@/lib/stores/pendingChangesStore';
import { LensesProvider } from '@/contexts/LensesContext';
import { useSplitViewAwareParams } from '@/hooks/useSplitViewAwareParams';
import { useSplitViewContext } from '@/components/layout/SplitViewContent';
import TemplateOutOfSyncBanner from '@/components/template/TemplateOutOfSyncBanner';
import { useAutoSave } from '@/hooks/useAutoSave';

export default function BlueprintItemPage() {
  const { itemId } = useSplitViewAwareParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const { updateTabTitle, updateTabUnsavedChanges, requestTabClose, tabs } = useTabContext();
  const splitViewContext = useSplitViewContext();
  const isSplitView = splitViewContext?.isSplitView ?? false;
  const { selectedGame } = useSimplifiedUserData();
  const { glossaryBlueprints, isLoading: contextLoading, loadData } = useGlossaryBlueprintsDataContext();
  
  const [blueprint, setBlueprint] = useState<GlossaryBlueprint | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pendingChangesVersion, setPendingChangesVersion] = useState(0);
  const hasTriedLoading = React.useRef(false);

  // Track unsaved changes and save handler for auto-save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const saveHandlerRef = useRef<(() => void) | undefined>();

  // Check if this is a new template creation
  const isNewTemplate = itemId === 'new';
  
  // Get folder from URL params for new templates
  const folderFromUrl = searchParams.get('folder');
  
  // Sync tab title with item name (handles both initial load and name changes)
  // Skip in split view mode - split view tabs are managed separately
  useTabTitleSync(
    !isNewTemplate ? blueprint?.name : undefined, 
    { disabled: isSplitView }
  );

  // Subscribe to pending changes to re-render when they change
  useEffect(() => {
    const unsubscribe = subscribeToPendingChanges(() => {
      setPendingChangesVersion(v => v + 1);
    });
    return unsubscribe;
  }, []);

  // Reset state immediately when itemId changes to prevent showing stale data
  useEffect(() => {
    if (!isNewTemplate) {
      setBlueprint(null);
      setIsLoading(true);
      setNotFound(false);
      hasTriedLoading.current = false;
    }
  }, [itemId, isNewTemplate]);

  // Handle new template setup
  useEffect(() => {
    if (isNewTemplate && selectedGame?.id && !isSplitView) {
      // Set up tab title for new template
      const tabId = location.pathname;
      updateTabTitle(tabId, 'New Template');
      setIsLoading(false);
    }
  }, [isNewTemplate, selectedGame?.id, location.pathname, updateTabTitle, isSplitView]);

  // Load template and merge with any pending changes
  useEffect(() => {
    if (isNewTemplate || !itemId) {
      if (!isNewTemplate && !itemId) {
        setNotFound(true);
        setIsLoading(false);
      }
      return;
    }

    // Try to find the item first - it might already be in the store/cache
    const item = glossaryBlueprints.find(bp => bp.id === itemId);
    
    if (item) {
      // Check for pending changes and merge them
      const pendingChange = getPendingChangeById(itemId);
      if (pendingChange?.pendingData) {
        console.log('[BlueprintItemPage] Found pending changes for item:', itemId);
        // Merge pending changes with the original item (pending data takes priority)
        const mergedItem: GlossaryBlueprint = {
          ...item,
          ...pendingChange.pendingData,
          // Preserve fields that shouldn't be overwritten
          id: item.id,
          game_id: item.game_id,
          created_at: item.created_at,
          created_by: item.created_by,
        };
        setBlueprint(mergedItem);
      } else {
        // Only update if blueprint changed to avoid unnecessary re-renders
        setBlueprint(prev => prev?.id === item.id ? prev : item);
      }
      setNotFound(false);
      setIsLoading(false);
    } else if (contextLoading) {
      // Item not found but still loading, wait
      setIsLoading(true);
    } else if (!hasTriedLoading.current) {
      // Item not found and loading completed, trigger a load once
      // This only happens when navigating directly to a URL without cached data
      hasTriedLoading.current = true;
      loadData();
    } else {
      // Already tried loading and item still not found
      setNotFound(true);
      setIsLoading(false);
    }
  }, [itemId, glossaryBlueprints, contextLoading, pendingChangesVersion, isNewTemplate, loadData]);

  // Redirect to templates if item not found (only for existing templates)
  useEffect(() => {
    if (notFound && !isLoading && !isNewTemplate) {
      console.warn(`Template ${itemId} not found or access denied. Redirecting to templates.`);
      navigate('/templates', { replace: true });
    }
  }, [notFound, isLoading, itemId, navigate, isNewTemplate]);


  // Handle saving a new template (create)
  const handleSave = useCallback(async (templateData: any) => {
    if (!selectedGame?.id) return;

    try {
      const newTemplate = await createTemplate(
        {
          ...templateData,
          game_id: selectedGame.id,
          folder: folderFromUrl || templateData.folder || null,
        },
        toast
      );

      // Refresh the templates to get the latest data
      await loadData();

      toast({
        title: 'Success',
        description: 'Template created successfully',
      });

      // Clear tab unsaved changes BEFORE navigation to prevent stale hasUnsavedChanges
      // from persisting through the wizard remount (key change causes old wizard to unmount,
      // losing its cleanup, while the tab retains the old unsaved changes flag)
      const activeTab = tabs.find(t => t.isActive);
      if (activeTab) {
        updateTabUnsavedChanges(activeTab.id, false);
      }

      // Navigate to the newly created template page
      navigate(`/templates/${newTemplate.id}`, { replace: true });
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: 'Error',
        description: 'Failed to create template',
        variant: 'destructive',
      });
    }
  }, [selectedGame?.id, folderFromUrl, toast, loadData, navigate, tabs, updateTabUnsavedChanges]);

  // Handle updating an existing template
  const handleUpdate = useCallback(async (updatedBlueprint: {
    id?: string;
    game_id?: string;
    name: string;
    description: string;
    tags: string[];
    types: string[];
    type: string;
    data: BlueprintData;
    settings: Record<string, unknown>;
    version?: {
      changes: Record<string, unknown>;
      version: number;
    };
  }) => {
    if (!itemId || !blueprint) return;

    try {
      // Don't calculate version here - let the API handle it to prevent duplicates
      const updatedTemplate = await updateTemplate(
        {
          ...updatedBlueprint,
          id: itemId,
          game_id: blueprint.game_id,
          // API will calculate the next version automatically
        },
        toast
      );

      // Clear any pending changes for this item since we just saved
      removePendingChange(itemId);
      console.log('[BlueprintItemPage] Cleared pending changes for item:', itemId);

      // Update the local blueprint state immediately with the returned data from the store
      // This ensures the UI reflects the latest state without needing a refresh
      setBlueprint(updatedTemplate);

      // Tab title update is handled by useTabTitleSync hook

      toast({
        title: 'Success',
        description: 'Template updated successfully',
      });
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: 'Error',
        description: 'Failed to update template',
        variant: 'destructive',
      });
    }
  }, [itemId, blueprint, toast, location.pathname, updateTabTitle]);

  const handleClose = useCallback(async () => {
    // Find the ACTUAL tab ID by looking for the active tab
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) {
      navigate('/templates');
      return;
    }
    
    const tabId = activeTab.id;
    
    // Request to close the tab (will show unsaved changes dialog if needed)
    const canClose = await requestTabClose(tabId);
    
    if (canClose) {
      // If the tab was closed, navigate to templates page
      navigate('/templates');
    }
  }, [tabs, requestTabClose, navigate]);

  const handleDelete = useCallback(async () => {
    if (!itemId || !blueprint || !selectedGame?.id) return;

    const confirmDialog = confirmation({
      title: 'Delete Template',
      description: `Are you sure you want to delete "${blueprint.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await deleteTemplate(itemId, selectedGame.id, toast);
          
          toast({
            title: 'Success',
            description: 'Template deleted successfully',
          });

          // Navigate back to templates page
          navigate('/templates');
          confirmDialog.dismiss();
        } catch (error) {
          console.error('Error deleting template:', error);
          confirmDialog.dismiss();
        }
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  }, [itemId, blueprint, selectedGame, toast, confirmation, navigate]);

  // Handle unsaved changes state from wizard
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean, saveHandler: () => void, discardHandler?: () => void) => {
    // Find the ACTUAL tab ID by looking for the active tab
    // Tab IDs may be stable IDs like 'tab-templates' rather than the pathname
    const activeTab = tabs.find(t => t.isActive);
    if (!activeTab) return;
    
    const tabId = activeTab.id;
    // Track for auto-save
    setHasUnsavedChanges(hasChanges);
    saveHandlerRef.current = saveHandler;
    // Update tab context
    updateTabUnsavedChanges(tabId, hasChanges, saveHandler, discardHandler);
  }, [tabs, updateTabUnsavedChanges]);

  // Enable auto-save based on user settings
  useAutoSave({
    hasUnsavedChanges,
    saveHandler: saveHandlerRef.current,
    enabled: !isNewTemplate, // Only auto-save for existing templates, not new ones
  });

  // Show loading state (only for existing templates)
  if (!isNewTemplate && (isLoading || contextLoading)) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading template..." />
      </div>
    );
  }

  // For new templates, check if we have required data
  if (isNewTemplate) {
    if (!selectedGame?.id) {
      return (
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="lg" text="Loading..." />
        </div>
      );
    }

    // Render wizard for new template (no initialData means create mode)
    return (
      <div className="h-full w-full bg-surface">
        <EditBlueprintWizard
          key="template-new"
          isOpen={true}
          inline={true}
          mode="create"
          onClose={handleClose}
          onSave={handleSave}
          onUnsavedChangesChange={handleUnsavedChangesChange}
        />
      </div>
    );
  }

  // If notFound or no blueprint, show loading while redirecting (redirect happens in useEffect)
  if (notFound || !blueprint) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Redirecting..." />
      </div>
    );
  }

  // CRITICAL: Ensure the blueprint ID matches the URL itemId before rendering
  // This prevents showing stale data when the blueprint state hasn't updated yet
  if (blueprint.id !== itemId) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Loading template..." />
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-surface flex flex-col">
      {/* Out of Sync Banner */}
      <div className="flex-shrink-0 px-6 pt-6">
        <TemplateOutOfSyncBanner blueprint={blueprint} />
      </div>
      
      {/* Blueprint Wizard */}
      <div className="flex-1 min-h-0">
        <EditBlueprintWizard
          key={itemId}
          isOpen={true}
          inline={true}
          mode="view"
          onClose={handleClose}
          onDelete={handleDelete}
          initialData={blueprint}
          onSave={handleUpdate}
          onUnsavedChangesChange={handleUnsavedChangesChange}
        />
      </div>
    </div>
  );
}
